import { VerificationType } from "@taskfortress/core/src/api/types"
import { useField, useForm } from "@taskfortress/core/src/form"
import {
  validateEmail,
  validateEmailUniqueness,
  validatePassword,
  validatePasswordConfirmation,
} from "@taskfortress/core/src/form/validation"
import { useCallback, useState } from "react"
import { Alert as RNAlert } from "react-native"
import { useAuth } from "../auth"
import { BaseScreen, Button, Text, TextLink } from "../components"
import { AuthForm, AuthLayout, GenerateMasterKey } from "../components/auth"
import { TextInput } from "../components/form"

type SignupStep = "credentials" | "masterKey"

export const SignupScreen = ({ navigation }: any) => {
  const [currentStep, setCurrentStep] = useState<SignupStep>("credentials")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { requestVerification, checkEmail } = useAuth()

  // Form fields for step 1
  const emailField = useField({
    name: "email",
    initialValue: "",
    validate: validateEmail,
    validateAsync: (email: string) =>
      validateEmailUniqueness(email, checkEmail),
    validateAsyncDebounceMs: 500,
  })

  const passwordField = useField({
    name: "password",
    initialValue: "",
    validate: validatePassword,
  })

  const confirmPasswordField = useField({
    name: "confirmPassword",
    initialValue: "",
    validate: (value: string) =>
      validatePasswordConfirmation(value, {
        password: passwordField.value || "",
      }),
  })

  // Form management for step 1
  const form = useForm({
    fields: [emailField, passwordField, confirmPasswordField],
  })

  // Handle step 1: Validate credentials and proceed to master key generation
  const handleNextStep = useCallback(async () => {
    if (!form.valid) {
      RNAlert.alert("Error", "Please fix the errors in the form")
      return
    }

    setError(null)
    setCurrentStep("masterKey")
  }, [form.valid])

  // Handle step 2: Complete signup with generated master key
  const handleMasterKeyConfirmed = useCallback(
    async (masterKey: string) => {
      setError(null)
      setIsLoading(true)

      try {
        // Request verification code first
        const result = await requestVerification(
          emailField.value!,
          VerificationType.SIGNUP,
        )

        if (result.success && result.verificationToken) {
          // Navigate to verification screen with all the data
          navigation.navigate("VerifyCode", {
            email: emailField.value,
            password: passwordField.value,
            masterKey: masterKey,
            verificationToken: result.verificationToken,
            verificationType: VerificationType.SIGNUP,
          })
        } else {
          throw new Error(
            "Failed to send verification code. Please check your email address.",
          )
        }
      } catch (err: any) {
        console.error("Verification request error:", err)
        setError(err.message || "Failed to send verification code.")
      } finally {
        setIsLoading(false)
      }
    },
    [emailField.value, passwordField.value, requestVerification, navigation],
  )

  // Handle going back to step 1
  const handleBackToCredentials = useCallback(() => {
    setCurrentStep("credentials")
    setError(null)
  }, [])

  // Render step 1: Credentials input
  if (currentStep === "credentials") {
    return (
      <BaseScreen>
        <AuthLayout>
          <Text className="text-2xl font-bold mb-2 text-center">
            Create Account
          </Text>
          <Text className="text-lg text-typography-500 mb-8 text-center">
            Sign up for TaskFortress
          </Text>

          <AuthForm onSubmit={handleNextStep}>
            <TextInput
              name="email"
              label="Email"
              placeholder="abc@example.com"
              value={emailField.value}
              handleChange={emailField.handleChange}
              error={emailField.error}
              disabled={isLoading}
            />

            <TextInput
              name="password"
              label="Password"
              placeholder="********"
              value={passwordField.value}
              handleChange={passwordField.handleChange}
              error={passwordField.error}
              type="password"
              disabled={isLoading}
            />

            <TextInput
              name="confirmPassword"
              label="Confirm Password"
              placeholder="********"
              value={confirmPasswordField.value}
              handleChange={confirmPasswordField.handleChange}
              error={confirmPasswordField.error}
              type="password"
              disabled={isLoading}
            />

            <Button
              onPress={handleNextStep}
              disabled={isLoading || !form.valid}
              isLoading={isLoading}
            >
              Next: Generate Master Key
            </Button>

            <TextLink
              onPress={() => navigation.navigate("Login")}
              disabled={isLoading}
              className="text-center mx-auto"
            >
              Already have an account? Login
            </TextLink>
          </AuthForm>
        </AuthLayout>
      </BaseScreen>
    )
  }

  // Render step 2: Master key generation
  return (
    <BaseScreen>
      <AuthLayout>
        <Text className="text-2xl font-bold mb-2 text-center">
          Master Key Setup
        </Text>

        <GenerateMasterKey
          onMasterKeyConfirmed={handleMasterKeyConfirmed}
          onBack={handleBackToCredentials}
          isLoading={isLoading}
          error={error}
        />
      </AuthLayout>
    </BaseScreen>
  )
}
